import { Controller } from "@hotwired/stimulus"

// Marks the contents link for the part of the monograph being read
// (AHPA-83). The current part is the last heading that has scrolled up to its
// own scroll-margin-top (which clears the sticky navbar), so the line moves
// with the navbar's height instead of a hard-coded offset. aria-current carries
// the state; the link classes style it.
export default class extends Controller {
  static targets = ["link"]

  connect() {
    this.headings = this.linkTargets
      .map((link) => document.getElementById(link.hash.slice(1)))
      .filter(Boolean)
    // Scrolling and resizing both move headings; update once per frame.
    this.onChange = () => {
      if (this.frame) return
      this.frame = requestAnimationFrame(() => {
        this.frame = null
        this.update()
      })
    }
    window.addEventListener("scroll", this.onChange, { passive: true })
    window.addEventListener("resize", this.onChange)
    this.update()
  }

  disconnect() {
    window.removeEventListener("scroll", this.onChange)
    window.removeEventListener("resize", this.onChange)
    if (this.frame) cancelAnimationFrame(this.frame)
    this.frame = null
  }

  update() {
    const current = this.atBottom ? this.headings.at(-1) : this.lastPassedHeading
    const currentLink =
      current && this.linkTargets.find((link) => link.hash === `#${current.id}`)
    this.linkTargets.forEach((link) => {
      if (link === currentLink) {
        link.setAttribute("aria-current", "location")
      } else {
        link.removeAttribute("aria-current")
      }
    })
    // Only when the mark moves, so a reader can still scroll the sidebar.
    if (currentLink && currentLink !== this.currentLink) {
      this.revealInSidebar(currentLink)
    }
    this.currentLink = currentLink
  }

  // The last heading at or above its scroll-margin line; the first heading
  // before any has been reached, so something is always marked.
  get lastPassedHeading() {
    const passed = this.headings.filter((heading) => {
      const margin = parseFloat(getComputedStyle(heading).scrollMarginTop) || 0
      return heading.getBoundingClientRect().top <= margin + 1
    })
    return passed.at(-1) || this.headings[0]
  }

  // Short final sections can never scroll up to the line, so at the bottom of
  // the page mark the last one. A page that doesn't scroll has no bottom to
  // reach, so it keeps the first.
  get atBottom() {
    const { scrollHeight } = document.documentElement
    const canScroll = scrollHeight > window.innerHeight
    return canScroll && window.innerHeight + window.scrollY >= scrollHeight - 2
  }

  // When the contents are taller than the sidebar, scroll the sidebar (never
  // the page) just enough to show the link, like block: "nearest". Rounded up
  // because scrollTop snaps to whole pixels.
  revealInSidebar(link) {
    const sidebar = this.element
    if (sidebar.scrollHeight <= sidebar.clientHeight) return

    const sidebarBox = sidebar.getBoundingClientRect()
    const linkBox = link.getBoundingClientRect()
    if (linkBox.top < sidebarBox.top) {
      sidebar.scrollTop -= Math.ceil(sidebarBox.top - linkBox.top)
    } else if (linkBox.bottom > sidebarBox.bottom) {
      sidebar.scrollTop += Math.ceil(linkBox.bottom - sidebarBox.bottom)
    }
  }
}
